"use client";

import React, { useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { Tooltip } from "../Tooltip";

export interface CopyToClipboardProps {
  /**
   * The value to copy to clipboard. If not provided, will use the text content of children
   */
  value?: string;
  /**
   * The content to display
   */
  children: React.ReactNode;
  /**
   * Optional className for the container
   */
  className?: string;
  /**
   * Size of the copy button, defaults to "sm"
   */
  size?: "sm" | "md";
  /**
   * Position of the copy icon relative to children, defaults to "right"
   */
  iconPosition?: "left" | "right";
}

export function CopyToClipboard({
  value,
  children,
  className,
  size = "sm",
  iconPosition = "right",
}: CopyToClipboardProps) {
  const [showTooltip, setShowTooltip] = useState(false);

  const handleCopy = () => {
    const textToCopy = value || (typeof children === "string" ? children : "");
    navigator.clipboard.writeText(textToCopy);
    setShowTooltip(true);
    setTimeout(() => {
      setShowTooltip(false);
    }, 2000);
  };

  const iconSize = size === "sm" ? 14 : 16;

  const copyButton = (
    <Button variant="icon" size={size} onPress={handleCopy} className="!px-2 relative" style={{ top: "0.5px" }}>
      <Icon name="Copy" size={iconSize} />
    </Button>
  );

  const tooltipButton = showTooltip ? <Tooltip content="Copied!">{copyButton}</Tooltip> : copyButton;

  return (
    <div className={twMerge("inline-flex items-center gap-0.5", className)}>
      {iconPosition === "left" && tooltipButton}
      {children}
      {iconPosition === "right" && tooltipButton}
    </div>
  );
}
